import { AddressDetails, AddressPlaceType } from '@pedix-workspace/utils';
import { GoogleMapsAddressDetailsParams } from './googe-maps.types';

export function getGoogleMapsAddressDetails({
  place,
  selectedPrediction,
  freeSearch,
  geolocation,
}: GoogleMapsAddressDetailsParams): AddressDetails {
  const addressComponents = place.addressComponents || [];
  const useCustomLocation = Boolean(geolocation);

  const street =
    addressComponents.find(component => component.types?.includes('route'))?.shortText || '';
  const streetNumber =
    addressComponents.find(component => component.types?.includes('street_number'))?.shortText ||
    '';

  const state = addressComponents.find(component =>
    component.types?.includes('administrative_area_level_1'),
  )?.shortText;
  const locality = addressComponents.find(component =>
    component.types?.includes('locality'),
  )?.longText;
  const country = addressComponents.find(component =>
    component.types?.includes('country'),
  )?.longText;
  const postalCode = addressComponents.find(component =>
    component.types?.includes('postal_code'),
  )?.shortText;
  const neighborhood = addressComponents.find(component =>
    component.types?.includes('neighborhood'),
  )?.shortText;
  const intersection = addressComponents.find(component =>
    component.types?.includes('intersection'),
  )?.shortText;

  const lat = geolocation ? geolocation.lat : place.location.latitude;
  const lng = geolocation ? geolocation.lng : place.location.longitude;
  const coordinates: google.maps.LatLngLiteral = { lat, lng };

  const suggestedPlaceType = getSuggestedPlaceType(place.types);
  const suggestedPlaceName = place.types.includes('establishment')
    ? selectedPrediction?.title.split(',')[0]
    : undefined;

  const fullAddressComponents = [];

  if (useCustomLocation && freeSearch) {
    fullAddressComponents.push(freeSearch);
  } else if (suggestedPlaceName || street) {
    if (suggestedPlaceName) {
      fullAddressComponents.push(suggestedPlaceName);
    }
    if (street) {
      fullAddressComponents.push(streetNumber ? `${street} ${streetNumber}` : street);
    }
  } else if (intersection) {
    fullAddressComponents.push(intersection);
  } else if (freeSearch) {
    fullAddressComponents.push(freeSearch);
  }
  if (locality) {
    fullAddressComponents.push(locality);
  }
  if (state) {
    fullAddressComponents.push(state);
  }
  if (country) {
    fullAddressComponents.push(country);
  }

  const fullAddress = fullAddressComponents.join(', ');
  const urlQuery = useCustomLocation ? `${lat},${lng}` : encodeURI(fullAddress.replace(/ /g, '+'));
  const url = `https://maps.google.com/?q=${urlQuery}`;

  return {
    freeSearch: freeSearch || undefined,
    fullAddress,
    url,
    street,
    streetNumber,
    state,
    locality,
    country,
    postalCode,
    neighborhood,
    coordinates,
    useCustomLocation,
    suggestedPlaceType,
    suggestedPlaceName,
  };
}

function getSuggestedPlaceType(types: string[]): AddressPlaceType {
  const typesSet = new Set(types);

  if (typesSet.has('apartment_building') || typesSet.has('apartment_complex')) {
    return 'building';
  }
  if (typesSet.has('condominium_complex') || typesSet.has('housing_complex')) {
    return 'housing-state';
  }
  if (
    typesSet.has('store') ||
    typesSet.has('shopping_mall') ||
    typesSet.has('market') ||
    typesSet.has('corporate_office')
  ) {
    return 'business';
  }
  if (typesSet.has('point_of_interest')) {
    return 'other';
  }
  return 'house';
}
